import { createSlice } from "@reduxjs/toolkit";
import { PURGE } from "redux-persist";
import {
  getCategoryDataSummaryPending,
  getCategoryDataSummaryFulfilled,
  getCategoryDataSummaryRejected,
  getFinCategoryDataSummaryPending,
  getFinCategoryDataSummaryFulfilled,
  getFinCategoryDataSummaryRejected,
  getBusinessPlanningDataPending,
  getBusinessPlanningDataFulfilled,
  getBusinessPlanningDataRejected,
  clearBusinessPlanningData,
  getBusinessZonesDataPending,
  getBusinessZonesDataFulfilled,
  getBusinessZonesDataRejected,
  clearBusinessZonesData,
} from "./analyticsStateActions";
import {
  FinancialCategoryEnumModel,
  ICategoryDataSummaryModel,
  IBusinessPlanningDataModel,
  IBusinessZonesResponse,
} from "../../../types/types";
import { IFinancialCategoryDataSummaryModel } from "../../../types/models";

interface CategoryState {
  loading: boolean;
  data: ICategoryDataSummaryModel[] | null;
  error: string | null;
}

interface IFinancialCategoryState {
  loading: boolean;
  data: IFinancialCategoryDataSummaryModel[] | null;
  error: string | null;
  categoryId?: FinancialCategoryEnumModel;
}

interface IBusinessPlanningState {
  loading: boolean;
  data: IBusinessPlanningDataModel | null;
  error: string | null;
}

interface IBusinessZonesState {
  loading: boolean;
  data: IBusinessZonesResponse | null;
  error: string | null;
}

interface AnalyticsState {
  categories: {
    [categoryId: string]: CategoryState;
  };
  financialCategoryState: IFinancialCategoryState;
  businessPlanning: IBusinessPlanningState;
  businessZones: IBusinessZonesState;
  loading: boolean;
}

const initialState: AnalyticsState = {
  categories: {},
  financialCategoryState: {
    loading: false,
    data: null,
    error: null,
  },
  businessPlanning: {
    loading: false,
    data: null,
    error: null,
  },
  businessZones: {
    loading: false,
    data: null,
    error: null,
  },
  loading: false,
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(PURGE, (state) => {
      state.categories = {};
      state.financialCategoryState = {
        loading: false,
        data: null,
        error: null,
      };
      state.businessPlanning = {
        loading: false,
        data: null,
        error: null,
      };
      state.businessZones = {
        loading: false,
        data: null,
        error: null,
      };
      state.loading = false;
    });

    builder
      .addCase(getCategoryDataSummaryPending, (state, action) => {
        const categoryId = action.payload;
        state.categories[categoryId] = {
          loading: true,
          data: null,
          error: null,
        };
        state.loading = true;
      })
      .addCase(getCategoryDataSummaryFulfilled, (state, action) => {
        const { categoryId, data } = action.payload;
        state.categories[categoryId] = {
          loading: false,
          data,
          error: null,
        };
        state.loading = false;
      })
      .addCase(getCategoryDataSummaryRejected, (state, action) => {
        const { categoryId, error } = action.payload;
        state.categories[categoryId] = {
          loading: false,
          data: null,
          error,
        };
        state.loading = false;
      })
      .addCase(getFinCategoryDataSummaryPending, (state, action) => {
        state.financialCategoryState.categoryId = action.payload;
        state.financialCategoryState.loading = true;
        state.financialCategoryState.data = [];
        state.financialCategoryState.error = null;
      })
      .addCase(getFinCategoryDataSummaryFulfilled, (state, action) => {
        const { categoryId, data } = action.payload;
        state.financialCategoryState.categoryId = categoryId;
        state.financialCategoryState.loading = false;
        state.financialCategoryState.data = data;
        state.financialCategoryState.error = null;
      })
      .addCase(getFinCategoryDataSummaryRejected, (state, action) => {
        const { categoryId, error } = action.payload;
        state.financialCategoryState.categoryId = categoryId;
        state.financialCategoryState.loading = false;
        state.financialCategoryState.data = [];
        state.financialCategoryState.error = error;
      })
      // Business Planning
      .addCase(getBusinessPlanningDataPending, (state) => {
        if (!state.businessPlanning) {
          state.businessPlanning = { loading: false, data: null, error: null };
        }
        state.businessPlanning.loading = true;
        state.businessPlanning.error = null;
      })
      .addCase(getBusinessPlanningDataFulfilled, (state, action) => {
        if (!state.businessPlanning) {
          state.businessPlanning = { loading: false, data: null, error: null };
        }
        state.businessPlanning.loading = false;
        state.businessPlanning.data = action.payload;
        state.businessPlanning.error = null;
      })
      .addCase(getBusinessPlanningDataRejected, (state, action) => {
        if (!state.businessPlanning) {
          state.businessPlanning = { loading: false, data: null, error: null };
        }
        state.businessPlanning.loading = false;
        state.businessPlanning.error = action.payload;
      })
      .addCase(clearBusinessPlanningData, (state) => {
        if (!state.businessPlanning) {
          state.businessPlanning = { loading: false, data: null, error: null };
        }
        state.businessPlanning.data = null;
        state.businessPlanning.error = null;
      })
      // Business Zones
      .addCase(getBusinessZonesDataPending, (state) => {
        if (!state.businessZones) {
          state.businessZones = { loading: false, data: null, error: null };
        }
        state.businessZones.loading = true;
        state.businessZones.error = null;
      })
      .addCase(getBusinessZonesDataFulfilled, (state, action) => {
        if (!state.businessZones) {
          state.businessZones = { loading: false, data: null, error: null };
        }
        state.businessZones.loading = false;
        state.businessZones.data = action.payload;
        state.businessZones.error = null;
      })
      .addCase(getBusinessZonesDataRejected, (state, action) => {
        if (!state.businessZones) {
          state.businessZones = { loading: false, data: null, error: null };
        }
        state.businessZones.loading = false;
        state.businessZones.error = action.payload;
      })
      .addCase(clearBusinessZonesData, (state) => {
        if (!state.businessZones) {
          state.businessZones = { loading: false, data: null, error: null };
        }
        state.businessZones.data = null;
        state.businessZones.error = null;
      });
  },
});

export default analyticsSlice.reducer;
